import React, { useState } from "react";
import { Modal, FormActions, Input, PictureUpload } from "../../shared";
import { Icons } from "../../shared/Icons";
import { professionalsApi } from "../../../services/professionalsApi";
import { cleanPhoneNumber } from "../../../utils/phoneUtils";

interface ProfessionalFormData {
  name: string;
  email: string;
  phoneNumber: string;
  picture?: string;
}

interface CreateProfessionalModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  editingProfessional?: {
    id: number;
    companyId: number;
    name: string;
    email?: string;
    phoneNumber?: string;
    picture?: string;
  } | null;
}

export const CreateProfessionalModal: React.FC<
  CreateProfessionalModalProps
> = ({ isOpen, onClose, onSuccess, editingProfessional }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [formData, setFormData] = useState<ProfessionalFormData>({
    name: editingProfessional?.name || "",
    email: editingProfessional?.email || "",
    phoneNumber: editingProfessional?.phoneNumber || "",
    picture: editingProfessional?.picture || "",
  });

  React.useEffect(() => {
    if (editingProfessional) {
      setFormData({
        name: editingProfessional.name,
        email: editingProfessional.email || "",
        phoneNumber: editingProfessional.phoneNumber || "",
        picture: editingProfessional.picture || "",
      });
    } else {
      setFormData({
        name: "",
        email: "",
        phoneNumber: "",
        picture: "",
      });
    }
    setError(null);
  }, [editingProfessional, isOpen]);

  const handleSubmit = async () => {
    // Validation
    if (!formData.name.trim()) {
      setError("Nome é obrigatório");
      return;
    }

    setLoading(true);
    setError(null);

    // Clean phone number before submission
    const cleanedData: any = {
      ...formData,
      phoneNumber: formData.phoneNumber
        ? cleanPhoneNumber(formData.phoneNumber)
        : "",
    };
    
    // Only include avatar if it's set (not empty)
    if (!cleanedData.picture) {
      delete cleanedData.picture;
    }

    try {
      let response;
      if (editingProfessional) {
        response = await professionalsApi.updateProfessional(
          editingProfessional.id,
          cleanedData
        );
      } else {
        response = await professionalsApi.createProfessional(cleanedData);
      }

      if (response.success) {
        setLoading(false);
        onSuccess();
        onClose();
      } else {
        setError(response.error || "Falha ao salvar profissional");
        setLoading(false);
      }
    } catch (err) {
      setError("Falha ao salvar profissional");
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      name: "",
      email: "",
      phoneNumber: "",
      picture: "",
    });
    setError(null);
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={
        editingProfessional ? "Editar Profissional" : "Criar Novo Profissional"
      }
      icon={<Icons.Person sx={{ fontSize: 24 }} />}
    >
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-center gap-3 mb-6">
          <Icons.Warning sx={{ fontSize: 24, color: "#b91c1c" }} />
          <span className="text-red-800 font-medium">{error}</span>
        </div>
      )}

      <div className="space-y-6">
        {/* Avatar Upload */}
        <PictureUpload
          label="Foto do Profissional"
          value={formData.picture}
          currentImage={editingProfessional?.picture}
          onChange={(base64) =>
            setFormData({ ...formData, picture: base64 })
          }
          onRemove={() => setFormData({ ...formData, picture: "" })}
          disabled={loading}
          previewSize="lg"
        />

        {/* Name Field */}
        <Input
          id="name"
          label="Nome Completo"
          type="text"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              handleSubmit();
            } else if (e.key === "Escape") {
              handleClose();
            }
          }}
          required
          disabled={loading}
          placeholder="Ex: Maria Silva"
        />

        {/* Email and Phone */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            id="email"
            label="E-mail"
            type="email"
            value={formData.email}
            onChange={(e) =>
              setFormData({ ...formData, email: e.target.value })
            }
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                handleSubmit();
              }
            }}
            disabled={loading}
            placeholder="maria@exemplo.com"
          />

          <Input
            id="phone"
            label="Telefone"
            type="tel"
            value={formData.phoneNumber}
            onChange={(e) =>
              setFormData({ ...formData, phoneNumber: e.target.value })
            }
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                handleSubmit();
              }
            }}
            disabled={loading}
            placeholder="+55 11 99999-9999"
          />
        </div>

        {/* Form Actions */}
        <FormActions
          onCancel={handleClose}
          onSubmit={handleSubmit}
          loading={loading}
          isEditing={!!editingProfessional}
          submitType="button"
          submitText={
            editingProfessional
              ? "Atualizar Profissional"
              : "Criar Profissional"
          }
        />
      </div>
    </Modal>
  );
};
